class ProductSwatches extends HTMLElement {
  constructor() {
    super();

    if (this.hasAttribute('data-lazyload-swatches-tag')) {
      this.groupTag = this.dataset.lazyloadSwatchesTag;
      this.productHandle = this.dataset.productHandle;
      this.lazyload();
    } else {
      this.init();
    }
  }

  init() {
    this.setEls();
    this.setImagesLoaded();
    this.setData();
    this.listeners();
  }

  lazyload() {
    const collectedTitles = new Set();
    const MAX_BATCHES = 20;

    const buildSuggestUrl = (excluded) => {
      const excludePart = excluded.length
        ? excluded.map(t => `-title:"${String(t).replace(/"/g, '\\"')}"`).join(' AND ') + ' AND ' : '';
      const q = `${excludePart}tag:"${this.groupTag}"`;
      return `${Shopify.routes.root}search/suggest?q=${encodeURIComponent(q)}&section_id=product-swatches`;
    };

    const fetchNext = (isFirstBatch) => {
      const url = buildSuggestUrl([...collectedTitles]);

      return fetch(url)
        .then((response) => {
          this.requestResponse = response;
          return response.text().then((text) => ({ ok: response.ok, text }));
        })
        .then(({ ok, text }) => {
          if (!ok) throw new Error(`${this.requestResponse.status}: ${text}`);

          const domParsed = new DOMParser().parseFromString(text, 'text/html');
          const domParsedEl = domParsed.querySelector('#shopify-section-product-swatches');
          if (!domParsedEl) return { done: true };
          const batchJsonEl = domParsedEl.querySelector('[data-swatches-json]');
          if (batchJsonEl && batchJsonEl.textContent) {
            const mainJsonEl = this.querySelector('[data-swatches-json]') || (() => {
              const n = document.createElement('script');
              n.type = 'application/json';
              n.setAttribute('data-swatches-json', '');
              this.appendChild(n);
              return n;
            })();
            const existing = mainJsonEl.textContent ? JSON.parse(mainJsonEl.textContent) : {};
            const incoming = JSON.parse(batchJsonEl.textContent);
            Object.assign(existing, incoming);
            mainJsonEl.textContent = JSON.stringify(existing);
          }

          const itemEls = Array.from(domParsedEl.querySelectorAll('[data-swatch-item]'));
          const titles = itemEls.map(a =>
            (a.textContent || '').trim() ||
            a.getAttribute('data-swatch-color') ||
            a.getAttribute('data-swatch-item') ||
            ''
          );

          if (isFirstBatch) {
            this.innerHTML = domParsedEl.innerHTML;

            /* 10+ variants */
            const detailsEl = this.querySelector('details#swatch-wrap');
            if (detailsEl) {
              const summaryEl = detailsEl.querySelector('summary p');
              const activeEl =
                this.querySelector(`[data-swatch-item="${this.productHandle}"]`) ||
                this.querySelector('[data-swatch-item]');
              if (summaryEl && activeEl) {
                summaryEl.textContent =
                  (activeEl.textContent || '').trim() ||
                  activeEl.getAttribute('data-swatch-color') ||
                  activeEl.getAttribute('data-swatch-item') ||
                  '';
              }
            } else {
              const active = this.querySelector(`[data-swatch-item="${this.productHandle}"]`);
              if (active) active.classList.add('product-swatches__item--active');
            }
          } else {

            const targetDetails = this.querySelector('details#swatch-wrap');
            const targetWrap = targetDetails ? targetDetails : this.querySelector('#swatch-wrap');
            if (!targetWrap) return { done: true };

            itemEls.forEach(a => {
              if (targetDetails) this.normalizeSwatchTag(a);
              targetWrap.appendChild(a);
            });
          }

          titles.forEach(t => { if (t) collectedTitles.add(t); });
          const moreComing = titles.length >= 10;
          return { done: !moreComing };
        });
    };

    let chain = Promise.resolve();
    for (let i = 0; i < MAX_BATCHES; i++) {
      chain = chain.then((prev) => {
        if (prev && prev.done) return prev;
        return fetchNext(i === 0);
      });
    }

    chain.then(() => {
      requestAnimationFrame(() => {
        window.swipers && window.swipers();
        this.init();
      });
    }).catch((err) => {
      requestAnimationFrame(() => {
        window.swipers && window.swipers();
        this.init();
      });
    });
  }

  setEls() {
    this.cardEl = this.closest('[data-product-card]');
    this.isProductCard = this.closest('[data-product-card]') !== null;
    this.els = this.querySelectorAll('[data-swatch-item]');

    if (!this.cardEl) return;

    this.badgeEl = this.cardEl.querySelector('[data-product-card-badge]');
    this.badgeImageEl = this.cardEl.querySelector('[data-product-card-badge-image]');
    this.imageEls = this.cardEl.querySelectorAll('[data-product-card-image]');
    this.linkEl = this.cardEl.querySelector('[data-product-card-url]');
    this.mediaEl = this.cardEl.querySelector('[data-product-card-media]');
    this.priceCompareEl = this.cardEl.querySelector('[data-product-card-price-compare]');
    this.priceRegularEl = this.cardEl.querySelector('[data-product-card-price-regular]');
    this.titleEL = this.cardEl.querySelector('[data-product-card-title]');
    this.variantEl = this.cardEl.querySelector('[data-product-card-variant-name]');
  }

  setImagesLoaded() {
    this.imagesLoaded = {};
  }

  setData() {
    this.dataActive = null;
    const jsonEl = this.querySelector('[data-swatches-json]');
    if (!jsonEl) return;
    this.data = JSON.parse(jsonEl.textContent);
  }

  listeners() {
    if (!this.data) return;

    this.els.forEach(el => {
      el.addEventListener('click', e => {
        if (el.classList.contains('product-swatches__item--active')) return;
        e.preventDefault();
        this.updateProduct(el)
      });
    });
  }

  loadingStateBegin() {
    this.cardEl.classList.add('product-card--loading');
  }

  loadingStateEnd(imageLoaded, img) {
    if (imageLoaded) {
      setTimeout(() => {
        this.cardEl.classList.remove('product-card--loading');
      }, 200);
    } else {
      img.onload = () => {
        setTimeout(() => {
          this.cardEl.classList.remove('product-card--loading');
        }, 200);
      }
    }
  }

  updateBadge() {
    // if active variant has no badge image, hide it:
    if (!this.dataActive.badge && this.badgeEl) {
      this.badgeEl.classList.add('hide');
      return;
    }

    // if active variant has a badge image, show it:
    if (this.dataActive.badge && this.badgeEl) {
      if (this.badgeEl.innerText !== this.dataActive.badge) this.badgeEl.innerText = this.dataActive.badge;
      this.badgeEl.classList.remove('hide');
    }

    // if active variant has a badge image, build it:
    if (this.dataActive.badge && !this.badgeEl) {
      this.badgeEl = document.createElement('div');
      this.badgeEl.classList.add('product-card__badge-text');
      this.badgeEl.setAttribute('data-product-card-badge', '');
      this.badgeEl.innerText = this.dataActive.badge;
      this.mediaEl.appendChild(this.badgeEl);
    }

    if (this.badgeEl) {
      this.badgeEl.classList = `badge badge--${this.dataActive.badge_is_sale ? 'sale' : this.dataActive.badge_handle}`;
    }
  }

  updateBadgeImage() {
    // if active variant has no badge image, hide it:
    if (!this.dataActive.badgeImage && this.badgeImageEl) {
      this.badgeImageEl.classList.add('hide');
      return;
    }

    // if active variant has a badge image, show it:
    if (this.dataActive.badgeImage && this.badgeImageEl) {
      if (this.badgeImageEl.src !== this.dataActive.badgeImage.src) this.badgeImageEl.src = this.dataActive.badgeImage.src;
      this.badgeImageEl.classList.remove('hide');
      return;
    }

    // if active variant has a badge image, build it:
    if (this.dataActive.badgeImage && !this.badgeImageEl) {
      this.badgeImageEl = document.createElement('img');
      this.badgeImageEl.classList.add('product__badge-image');
      this.badgeImageEl.setAttribute('data-product-card-badge-image', '');
      this.badgeImageEl.ariaHidden = 'true';
      this.badgeImageEl.role = 'presentation';
      this.badgeImageEl.src = this.dataActive.badgeImage.src;
      this.mediaEl.appendChild(this.badgeImageEl);
    }
  }

  updateImages() {
    this.imageEls.forEach((img, index) => {
      if (!this.dataActive.images[index]) {
        img.classList.add('hide');
        return
      } else {
        img.classList.remove('hide');
      }

      const srcEl = img.querySelector('[data-product-card-image-src]');
      const srcsetEl = img.querySelector('[data-product-card-image-srcset]');
      if (srcEl && this.dataActive.images[index]) srcEl.alt = this.dataActive.images[index].alt;
      if (srcEl && this.dataActive.images[index]) srcEl.src = this.dataActive.images[index].src;
      if (srcEl && srcEl.hasOwnProperty('srcset') && this.dataActive.images[index]) srcEl.srcset = this.dataActive.images[index].srcset;
      if (srcsetEl && this.dataActive.images[index]) srcsetEl.srcset = this.dataActive.images[index].srcsetLarge;

      const videoEl = this.cardEl.querySelector('video');
      if (videoEl) videoEl.classList.add('hide');
    });
  }

  updatePrice() {
    if (this.dataActive.priceCompareAtAmount > this.dataActive.priceAmount) {
      this.priceCompareEl.innerHTML = this.dataActive.priceCompareAt;
      this.priceCompareEl.classList.remove('hide');
      this.priceRegularEl.innerHTML = this.dataActive.price;
    } else {
      this.priceCompareEl.innerHTML = '';
      this.priceCompareEl.classList.add('hide');
      this.priceRegularEl.innerHTML = this.dataActive.price;
    }
  }

  updateProduct(el) {
    this.dataActive = this.data[el.dataset.swatchItem];

    if (!this.dataActive) return;

    publish(PUB_SUB_EVENTS.swatchChange, {
      source: this.cardEl ? 'product-card' : 'product-detail',
      data: this.dataActive,
      url: el.href,
      el: this.cardEl,
    });

    /* update active classes */
    const detailsEl = this.querySelector('details#swatch-wrap');
    if (!detailsEl) {
      this.querySelector('.product-swatches__item--active')?.classList.remove('product-swatches__item--active');
      el.classList.add('product-swatches__item--active');
    } else {
      /* 10+ variants */
      const summaryEl = detailsEl.querySelector('summary p');
      if (summaryEl) {
        summaryEl.textContent =
          (el.textContent || '').trim() ||
          el.getAttribute('data-swatch-color') ||
          el.getAttribute('data-swatch-item') ||
          '';
      }
    }


    if (!this.cardEl) return;

    const imageLoaded = el.hasAttribute('data-image-loaded');
    let img;

    /* make request for image to end loading state when its loaded */
    if (!imageLoaded) {
      img = new Image();
      img.src = this.dataActive.images[0].src;
      el.setAttribute('data-image-loaded', true);
    }

    /* build swatches */
    /* buildQuickAdd(el, this.dataActive.variants); */

    if (!imageLoaded) {
      this.loadingStateBegin();
    }

    const colorLabel = document.querySelector('.swatch__current-color');
    if (colorLabel) {
      const color = el.getAttribute('data-swatch-color');
      if (color) {
        colorLabel.textContent = `Color: ${color}`;
      }
    }

    /* update content */
    this.updateBadge();
    this.updateBadgeImage();
    this.updateImages();
    this.updatePrice();
    this.linkEl.href = this.dataActive.url;
    this.linkEl.title = `View ${this.dataActive.title}`;
    this.titleEL.innerText = this.dataActive.title.replace(/&lt;br&gt;/g, "\n").replace(/&#39;/g , "\'");
    if (this.variantEl) this.variantEl.innerText = this.dataActive.variantName;

    if (!imageLoaded) {
      this.loadingStateEnd(imageLoaded, img);
    }
  }
  normalizeSwatchTag(a) {
    a.querySelector('figure')?.remove();

    const clean = raw => {
    const title = String(raw || '').trim();
      return title.replace(/^\s*new\s*era[\s\-:]*/i, '').split("'")[0].trim();
    };

    let label = (a.textContent || '').trim() || a.getAttribute('title') || a.getAttribute('aria-label');
    if (!label) {
      const handle = a.getAttribute('data-swatch-item');
      const jsonEl = this.querySelector('[data-swatches-json]');
      if (handle && jsonEl && jsonEl.textContent) {
        try {
          const data = JSON.parse(jsonEl.textContent);
          const prod = data?.[handle] || {};
          const source = prod.title ?? '';
          label = clean(source);
        } catch {}
      }
    }

    if (!label) {
      const textish = (a.textContent || '').trim() || a.getAttribute('title') || a.getAttribute('aria-label') || '';
      label = clean(textish) || a.getAttribute('data-swatch-color') || a.getAttribute('data-swatch-item') || '';
    }

    a.textContent = label;
    a.style.textAlign = 'left';
    a.style.borderTop = '1px solid var(--gray-border)';
    a.style.padding = '8px 0';
    a.style.flexDirection = 'row';
  }
}

if (!customElements.get('product-swatches')) {
  customElements.define('product-swatches', ProductSwatches);
}

function updateColorLabel() {
  const el = document.getElementById('active-color-swatch');
  if (!el) return false;

  const summary = document.querySelector('details#swatch-wrap summary p');
  let text = summary?.textContent?.trim();

  if (!text) {
    const swatch =
      document.querySelector('.product-swatches__item--active') ||
      document.querySelector('.product-swatches__item');
    text =
      (swatch?.textContent || '').trim() ||
      swatch?.getAttribute('data-swatch-color') ||
      swatch?.getAttribute('data-swatch-item') ||
      '';
  }

  if (!text) return false;

  el.textContent = `Color: ${text}`;
  el.classList.remove('color__hidden');
  return true;
}

document.addEventListener('click', e => {
  if (e.target.closest('.product-swatches__item')) setTimeout(updateColorLabel, 0);
});

const obs = new MutationObserver(() => {
  if (updateColorLabel()) obs.disconnect();
});
obs.observe(document.body, { childList: true, subtree: true });

updateColorLabel();

